/**
 * Types du domaine devis.
 *
 * Ils décrivent le devis tel que le métier le comprend, pas tel que Postgres
 * le stocke : les trois portes manipulent ces types, l'adaptateur de données
 * se charge de la correspondance avec les colonnes.
 */

import type { InvoiceStatus, InvoiceType } from "../invoices/types.js";

/**
 * Une facture tirée du devis, réduite à ce qu'il faut pour savoir ce qui reste
 * à facturer : son type, son statut, ses totaux.
 */
export interface QuoteInvoiceRef {
  id: string;
  invoiceNumber: string;
  type: InvoiceType;
  status: InvoiceStatus;
  totalHt: number;
  totalTtc: number;
}

export type QuoteStatus =
  | "draft"
  | "sent"
  | "viewed"
  | "accepted"
  | "rejected"
  | "expired"
  | "cancelled";

/** Statuts depuis lesquels un devis peut encore être modifié. */
export const EDITABLE_STATUSES: readonly QuoteStatus[] = ["draft"];

export interface QuoteLine {
  id?: string;
  isSectionHeader: boolean;
  sectionTitle?: string | null;
  designation: string;
  unit: string;
  quantity: number;
  unitCost: number;
  unitPrice: number;
  tvaRate: number;
  notes?: string | null;
}

export interface QuoteTotals {
  totalHt: number;
  totalTva: number;
  totalTtc: number;
  totalCost: number;
  marginAmount: number;
  marginPercent: number;
}

export interface ClientSummary {
  id: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  /**
   * Sans elle, aucun écran ne peut dire au patron *à quelle adresse* part le
   * devis ou la relance qu'il confirme — et une confirmation qui ne nomme pas
   * son destinataire ne confirme pas grand-chose.
   */
  email: string | null;
}

/**
 * Nom d'affichage d'un client.
 *
 * Règle métier, pas détail de présentation : une entreprise se désigne par sa
 * raison sociale, un particulier par son nom. L'agent qui répond « le devis de
 * Dupont Peinture » doit nommer le client exactement comme l'écran, sinon le
 * patron ne reconnaît pas de quoi on lui parle.
 */
export function clientDisplayName(client: ClientSummary | null): string {
  if (!client) return "Client inconnu";
  if (client.companyName) return client.companyName;
  const full = [client.firstName, client.lastName].filter(Boolean).join(" ");
  return full || "Sans nom";
}

/** Vue liste : ce qu'il faut pour afficher une ligne de tableau, rien de plus. */
export interface QuoteSummary {
  id: string;
  quoteNumber: string;
  title: string;
  status: QuoteStatus;
  totals: QuoteTotals;
  client: ClientSummary | null;
  createdAt: string;
  sentAt: string | null;
}

/** Vue détail : l'en-tête, ses lignes, et le client complet. */
export interface QuoteDetail extends QuoteSummary {
  description: string | null;
  observations: string | null;
  siteAddressLine1: string | null;
  sitePostalCode: string | null;
  siteCity: string | null;
  validityDays: number;
  estimatedDurationDays: number | null;
  appliesReducedTva: boolean;
  reducedTvaRate: number;
  paymentConditions: string | null;
  warrantyText: string | null;
  specialConditions: string | null;
  lines: QuoteLine[];
  aiGenerated: boolean;
  /**
   * Pourquoi le client a dit non.
   *
   * `changer_statut_devis` refuse un refus sans motif, et se justifie ainsi :
   * « c'est cette donnée qui permet de savoir si vous perdez sur le prix ou
   * sur les délais ». La colonne était écrite depuis l'origine et relue nulle
   * part — donc la contrainte coûtait une saisie au patron sans rien lui
   * rendre. Elle remonte maintenant jusqu'à l'écran.
   */
  rejectionReason: string | null;
  /**
   * Le chantier que ce devis a ouvert, s'il a été accepté.
   *
   * Accepter un devis crée son chantier dans la même transaction — voir la
   * migration `20260301002800_project_from_accepted_quote`. L'écran a besoin
   * de cet identifiant pour emmener le patron sur le chantier au lieu de lui
   * annoncer qu'il existe quelque part.
   *
   * Nul pour un devis non accepté, et nul aussi pour les devis acceptés
   * **avant** cette migration : personne ne leur a créé de chantier, et en
   * fabriquer après coup poserait des chantiers que le patron a peut-être
   * déjà saisis à la main sous un autre nom.
   */
  openedProjectId: string | null;

  /**
   * La date de début annoncée au client, et la fin qui en découle.
   *
   * Déduites du planning par `computeAvailability`, puis **acceptées par le
   * patron** — ce n'est pas un calcul qui s'impose, c'est une proposition qu'il
   * retient. Elles voyagent jusqu'au chantier à l'acceptation du devis, et
   * c'est précisément parce que quelqu'un les a décidées qu'elles y ont leur
   * place ; voir `20260301003400_quote_expected_dates`.
   *
   * `expectedEndOn` reste nulle quand le devis ne porte aucune durée estimée :
   * on n'invente pas une fin.
   */
  expectedStartOn: string | null;
  expectedEndOn: string | null;

  /**
   * Les factures émises depuis ce devis, brouillons compris.
   *
   * Pour « Facturer et envoyer le solde » : le montant annoncé au patron
   * déduit les acomptes et situations déjà réclamés (`priorInvoiced`), et le
   * bouton se retire quand un solde existe déjà.
   */
  invoices: QuoteInvoiceRef[];
}

export interface QuoteFilter {
  status?: QuoteStatus[];
  clientId?: string;
  /** Devis envoyés dont la validité est dépassée. */
  expiredOnly?: boolean;
}
